// src/page/CreateReport.tsx
import React, { useState, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { InteractiveQuestion, MetabaseQuestion } from "@metabase/embedding-sdk-react";
import { API_BASE } from "../services/apiBase.ts";
import { createSavedItem, getAdminTenantHeader } from "../services/saved.ts";

class LocalSdkRetryBoundary extends React.Component<
  { onRetry: () => void; children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: any) {
    console.error("CreateReport SDK crashed; retrying once", error);
    window.setTimeout(() => {
      this.props.onRetry();
    }, 500);
  }

  render() {
    if (this.state.hasError) {
      return <div style={{ padding: 8, opacity: 0.8 }}>Reloading report builder...</div>;
    }

    return this.props.children;
  }
}

type CreateReportProps = {
  sourceMode?: "model" | "sql";
};

const CreateReport: React.FC<CreateReportProps> = ({ sourceMode = "model" }) => {
  const auth = useAuth();
  const rawToken = auth.user?.access_token;
  const email = auth.user?.profile?.email ?? undefined;

  const [collectionId, setCollectionId] = useState<number | null>(null);
  const [loadingCollection, setLoadingCollection] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkRetryKey, setSdkRetryKey] = useState(0);

  useEffect(() => {
    async function loadCollection() {
      if (!rawToken) {
        setLoadingCollection(false);
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/api/collection-ids`, {
          headers: {
            Authorization: `Bearer ${rawToken}`,
            ...(email ? { "X-User-Email": email } : {}),
            ...getAdminTenantHeader(),
          },
        });

        if (!res.ok) {
          throw new Error("Failed to load collection ids");
        }

        const data = await res.json();
        setCollectionId(data.saved_collection_id);
      } catch (err) {
        console.error("Failed to load collection id", err);
        setSaveError("Failed to load collection configuration");
      } finally {
        setLoadingCollection(false);
      }
    }

    loadCollection();
  }, [rawToken, email]);

  useEffect(() => {
    if (loadingCollection || collectionId == null) {
      setSdkReady(false);
      return;
    }

    const t = window.setTimeout(() => {
      setSdkReady(true);
    }, 1000);

    return () => window.clearTimeout(t);
  }, [loadingCollection, collectionId]);

  async function handleSaveQuestion(question: MetabaseQuestion) {
    if (!rawToken) {
      setSaveError("Missing access token; please sign in again.");
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);

      const mbId = question?.id;
      const name = question?.name || "Untitled report";

      if (!mbId) {
        throw new Error("Saved question is missing id from Metabase");
      }

      await createSavedItem(
        rawToken,
        { kind: "card", metabase_id: mbId, title: name },
        email,
      );
    } catch (err: any) {
      setSaveError(err?.message || "Failed to save report metadata");
    } finally {
      setSaving(false);
    }
  }

  if (!rawToken) {
    return <div>Missing access token; please sign in again.</div>;
  }

  return (
    <div className="rt-mb-container">
      {loadingCollection && (
        <div style={{ padding: 8, opacity: 0.8 }}>
          Loading report builder...
        </div>
      )}

      {!loadingCollection && collectionId == null && (
        <div style={{ padding: 8, color: "#ff6b6b" }}>
          No saved collection is configured for this tenant.
        </div>
      )}

      {!loadingCollection && collectionId != null && !sdkReady && (
        <div style={{ padding: 8, opacity: 0.8 }}>
          Preparing report builder...
        </div>
      )}

      {/* ✅ ONLY render SDK when ready */}
      {!loadingCollection && collectionId != null && sdkReady && (
        <LocalSdkRetryBoundary
          key={`boundary-${collectionId}-${sdkRetryKey}`}
          onRetry={() => setSdkRetryKey((v) => v + 1)}
        >
          <InteractiveQuestion
            key={`create-report-${sourceMode}-${collectionId}-${email ?? "user"}-${sdkRetryKey}`}
            questionId={sourceMode === "sql" ? "new-native" : "new"}
            targetCollection={collectionId}
            {...(sourceMode === "sql" ? {} : { entityTypes: ["model"] })}
            isSaveEnabled
            onSave={handleSaveQuestion}
            height="100%"
          />
        </LocalSdkRetryBoundary>
      )}

      {saveError && (
        <div style={{ padding: 8, color: "#ff6b6b" }}>{saveError}</div>
      )}

      {saving && (
        <div style={{ padding: 8, opacity: 0.8 }}>Saving…</div>
      )}
    </div>
  );
  };

export default CreateReport;
